import { type CalendarItem } from '../types'

/**
 * Everything a right-click can ask for, in one vocabulary for every kind of
 * item. Which of them an item actually offers is `buildCalendarMenuOptions`.
 */
export const CalendarMenuActionIds = {
  addToBoard: 'add-to-board',
  addToPlan: 'add-to-plan',
  copyLink: 'copy-link',
  delete: 'delete',
  duplicate: 'duplicate',
  openInNewTab: 'open-in-new-tab',
  rename: 'rename',
  /** Takes the moment off the calendar by moving it onto a board. */
  unschedule: 'unschedule',
} as const

export type CalendarMenuActionId =
  (typeof CalendarMenuActionIds)[keyof typeof CalendarMenuActionIds]

export type CalendarMenuOption =
  | Readonly<{
      type: 'action'
      id: CalendarMenuActionId
      label: string
      /** Drawn in the destructive colour, and never the first thing on the list. */
      isDestructive?: boolean
    }>
  | Readonly<{ type: 'separator' }>

/**
 * What the surface around the calendar can carry out. Every one of these opens
 * something the calendar does not own, so an action nobody can answer is left
 * off the menu rather than offered and ignored.
 */
export type CalendarMenuCapabilities = Readonly<{
  /** Gates both Add to Board and Unschedule, which are the same modal. */
  canAddToBoard?: boolean
  canAddToPlan?: boolean
  canDuplicateMoment?: boolean
  /**
   * Whether the reader may change what they are looking at. False leaves only
   * the ways to read the item — its page and a link to it — so a view-only
   * reader is never offered something the API would refuse.
   *
   * @default true
   */
  canEdit?: boolean
}>

export type CalendarItemAction = Readonly<{
  id: CalendarMenuActionId
  item: CalendarItem
}>

/** Answers what a person picked. The menu itself does nothing. */
export type OnCalendarItemAction = (action: CalendarItemAction) => void

/** What the grid needs to draw a menu at all. Absent means no menu. */
export type CalendarMenuProps = Readonly<{
  capabilities?: CalendarMenuCapabilities
  onItemAction: OnCalendarItemAction
}>
